// 'use client' is load-bearing, not decoration: this module calls React hooks,
// and it is reachable from the `@auctogon/ui/web` barrel. A Server Component
// importing ANY symbol from that barrel pulls this file into the RSC graph,
// where hooks don't exist — `next build` then fails with "You're importing a
// module that depends on useEffect into a React Server Component module".
// The client-side mirror of sharing.md § 3's root-barrel hygiene rule; guarded
// by web-barrel-client-directive.test.ts (BL-1124).
'use client';

/**
 * useOutsideClick — close a popup/menu/dropdown when a mousedown lands outside
 * every element in `refs`.
 *
 * Consolidates the byte-near-identical outside-click effects that Select
 * (`packages/ui`), OverflowMenu, DateTimePicker, and DatePicker (`admin-web`)
 * each hand-rolled. DOM-bound (uses `document` / `MouseEvent`), so it is
 * exported from the `@auctogon/ui/web` subpath, never the RN-safe root.
 *
 * The `refs` array is captured through a latest-ref so a fresh array literal on
 * every render doesn't churn the listener at reposition-frame rates (the
 * original DateTimePicker implementation's rationale, preserved).
 *
 * Options:
 *  - `enabled` (default `true`): when `false`, no listener is attached — pass
 *    the popup's `isOpen` so a closed popup does no work (Select / OverflowMenu
 *    gate on this; DatePicker / DateTimePicker leave it default because they
 *    only mount or matter while open).
 *  - `capture` (default `false`): attach on the capture phase. OverflowMenu
 *    needs this so the outside-click fires before a portaled descendant's own
 *    handler.
 *  - `ignore` (default none): an outside mousedown for which it returns `true`
 *    does not close. A non-modal side panel passes it so a click on a control
 *    behind the panel acts instead of dismissing (BL-2087,
 *    `isNonModalDismissTarget`). Absent, every outside mousedown closes — the
 *    popover callers' behaviour.
 */

import { useEffect, useRef, type RefObject } from 'react';

/** Options controlling when/how the outside-click listener attaches. */
export interface UseOutsideClickOptions {
  /** Attach the listener only while `true`. Default `true`. */
  enabled?: boolean;
  /** Listen on the capture phase instead of bubble. Default `false`. */
  capture?: boolean;
  /** Outside mousedowns for which this returns `true` are ignored. Default: none. */
  ignore?: (target: Element, event: MouseEvent) => boolean;
}

/**
 * Call `onClose` when a `mousedown` occurs outside every element in `refs`.
 *
 * @param refs Elements considered "inside" — a click within any of them is ignored.
 * @param onClose Invoked on an outside click.
 * @param options `enabled` / `capture` — see {@link UseOutsideClickOptions}.
 */
export function useOutsideClick(
  refs: Array<RefObject<HTMLElement | null>>,
  onClose: () => void,
  options: UseOutsideClickOptions = {},
): void {
  const { enabled = true, capture = false, ignore } = options;
  const refsRef = useRef(refs);
  const onCloseRef = useRef(onClose);
  const ignoreRef = useRef(ignore);
  useEffect(() => {
    refsRef.current = refs;
    onCloseRef.current = onClose;
    ignoreRef.current = ignore;
  });
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (refsRef.current.every((r) => !r.current || !r.current.contains(target))) {
        if (target instanceof Element && ignoreRef.current?.(target, e)) return;
        onCloseRef.current();
      }
    };
    document.addEventListener('mousedown', handler, capture);
    return () => document.removeEventListener('mousedown', handler, capture);
  }, [enabled, capture]);
}
